import { ArrowLeft, BadgeCheck, CircleAlert, DoorOpen, UtensilsCrossed } from "lucide-react";
import React, { useEffect, useState } from "react";

import { comprovante, lojaPublica, qrDaSaida } from "./api";
import { medidaDoItem, qtdDoItem } from "../../lib/unidadeVenda";

const dinheiro = (v) => `R$ ${Number(v || 0).toFixed(2).replace(".", ",")}`;
const hora = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : "";
};
const RECARREGA_MS = 5000;

function useRelogio() {
  const [agora, setAgora] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setAgora(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return agora;
}

// Comprovante do cliente (dono, 24/09/2026): nome, dados, o que pagou e o QR
// que o salão lê na saída. O status é AO VIVO (recarrega a cada 5 s) e o
// relógio corre — um print repassado fica parado no tempo, e o QR lido de
// novo depois da saída mostra "já foi embora".
export default function Comprovante({ codigo, voltar }) {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(null);
  const [loja, setLoja] = useState(null);
  const agora = useRelogio();
  const escuro = (() => { try { return localStorage.getItem("cardapio_theme") !== "light"; } catch { return true; } })();

  useEffect(() => {
    lojaPublica().then((r) => r.ok && setLoja(r.dados));
    let vivo = true;
    const carregar = async () => {
      const r = await comprovante(codigo);
      if (!vivo) return;
      if (r.ok) setDados(r.dados);
      else setErro(r.erro?.mensagem || "Comprovante não encontrado.");
    };
    carregar();
    const id = setInterval(carregar, RECARREGA_MS);
    return () => { vivo = false; clearInterval(id); };
  }, [codigo]);

  const fundo = escuro ? "bg-zinc-950 text-zinc-100" : "bg-zinc-50 text-zinc-900";
  const cartao = escuro ? "bg-zinc-900 border-zinc-800" : "bg-white border-zinc-200";
  if (erro && !dados) return <div className={`min-h-screen flex items-center justify-center p-8 text-center ${fundo}`}>{erro}</div>;
  if (!dados) return <div className={`min-h-screen ${fundo}`} />;

  const saiu = dados.saida;
  const deve = dados.em_aberto > 0;
  const status = saiu
    ? { cor: "bg-zinc-700", icone: DoorOpen, titulo: "SAÍDA LIBERADA", sub: `às ${hora(saiu.em)}${saiu.por ? ` · por ${String(saiu.por).split(" ")[0]}` : ""}` }
    : !dados.sessao_ativa
      // Mesa fechada/cancelada/transferida no caixa: o comprovante não prova mais nada
      ? { cor: "bg-zinc-700", icone: DoorOpen, titulo: "CONTA ENCERRADA", sub: "A mesa foi fechada no caixa" }
      : deve
        ? { cor: "bg-amber-500", icone: CircleAlert, titulo: `FALTA PAGAR ${dinheiro(dados.em_aberto)}`, sub: "Pague pelo celular ou no caixa antes de sair" }
        : { cor: "bg-emerald-600", icone: BadgeCheck, titulo: "PAGO", sub: "Mostre este QR na saída" };
  const Icone = status.icone;
  const logo = loja?.logo_url ? (loja.logo_url.startsWith("/uploads/") ? `/api${loja.logo_url}` : loja.logo_url) : null;

  return (
    <div className={`min-h-screen ${fundo}`} data-e2e="comprovante"
      data-estado={saiu ? "saiu" : !dados.sessao_ativa ? "encerrada" : deve ? "aberto" : "pago"}>
      <div className="max-w-md mx-auto px-4 py-5 space-y-3">
        <div className="flex items-center gap-3">
          {logo ? <img src={logo} alt="" className="w-11 h-11 rounded-xl object-cover bg-white" /> : (
            <span className="w-11 h-11 rounded-xl bg-primary/15 text-primary flex items-center justify-center"><UtensilsCrossed className="w-5 h-5" /></span>
          )}
          <div className="min-w-0">
            <p className="font-black truncate">{loja?.fantasy_name || loja?.company_name || "Comprovante"}</p>
            <p className="text-xs opacity-60">{dados.mesa?.nome} · comprovante de pagamento</p>
          </div>
        </div>

        <div className={`${status.cor} text-white rounded-3xl px-5 py-5 text-center`}>
          <Icone className="w-10 h-10 mx-auto mb-1" />
          <p className="text-2xl font-black tracking-tight" data-e2e="comprovante-status">{status.titulo}</p>
          <p className="text-sm opacity-90">{status.sub}</p>
          <p className="mt-3 inline-flex items-center gap-2 text-xs font-mono bg-black/20 rounded-full px-3 py-1" data-e2e="comprovante-relogio">
            <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
            agora {agora.toLocaleTimeString("pt-BR")}
          </p>
        </div>

        {!saiu && dados.sessao_ativa ? (
          <div className={`rounded-3xl border p-4 flex flex-col items-center ${cartao}`}>
            <img src={qrDaSaida(codigo)} alt="QR de saída" className="w-56 h-56 rounded-xl bg-white p-2" data-e2e="comprovante-qr" />
            <p className="text-xs opacity-60 mt-2 text-center">Este QR vale uma saída só. Depois de lido, mostra que você já foi embora.</p>
          </div>
        ) : null}

        <div className={`rounded-3xl border p-4 ${cartao}`}>
          <p className="text-lg font-black">{dados.pessoa?.nome}</p>
          {dados.pessoa?.cpf ? <p className="text-xs opacity-60">CPF {dados.pessoa.cpf}</p> : null}
          <div className="mt-3 space-y-1.5">
            {dados.pagamentos.map((p) => (
              <div key={p.id} className="flex items-center justify-between text-sm">
                <span>{p.paguei ? "Você pagou" : `Pago por ${p.por}`} · {hora(p.hora)}</span>
                <span className="font-bold tabular-nums">
                  {dinheiro(p.total)}{p.cashback > 0 ? <span className="text-xs text-emerald-500"> ({dinheiro(p.cashback)} cashback)</span> : null}
                </span>
              </div>
            ))}
          </div>
          {dados.itens_pagos.length ? (
            <div className={`mt-3 pt-3 border-t space-y-1 ${escuro ? "border-zinc-800" : "border-zinc-200"}`}>
              {dados.itens_pagos.map((i, idx) => (
                <div key={`${i.chave}-${idx}`} className="flex justify-between text-xs opacity-80">
                  {/* Quilo/litro (30/09/2026): tela sem login — vale o que o item traz ("1,25x" em vez de "1.25x") */}
                  <span className="truncate">{qtdDoItem(i.quantidade || 1, medidaDoItem(i))} {i.nome}</span>
                  <span className="tabular-nums">{dinheiro(i.valor)}</span>
                </div>
              ))}
            </div>
          ) : null}
          {dados.grupo.length ? (
            <p className="mt-3 text-xs opacity-70">
              Você pagou também por: {dados.grupo.map((g) => `${g.nome}${g.saiu ? " (saiu)" : ""}`).join(", ")}
            </p>
          ) : null}
        </div>

        {voltar && !saiu && dados.sessao_ativa ? (
          <button type="button" onClick={() => { window.location.hash = `#${voltar}`; }} data-e2e="comprovante-voltar"
            className="w-full h-12 rounded-full border-2 border-primary/40 text-primary font-bold flex items-center justify-center gap-1.5">
            <ArrowLeft className="w-4 h-4" /> Voltar para a mesa
          </button>
        ) : null}
      </div>
    </div>
  );
}
